import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
const samples = [{
  src: 'https://cdn.magicpatterns.com/uploads/842jC1rTaueHjSQKGNZSor/01_detectives_tabla_periodica.webp',
  caption: 'Detectives de la tabla periódica'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/nHNCrqhcxeyW4c7bFD5TpR/02_particulas_detectives_impostores.webp',
  caption: 'Partículas: detectives e impostores'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/xq8owfLgjrWoySWrkD5iLT/03_parque_clasificacion_materia.webp',
  caption: 'Clasificación de la materia'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/r6UdwC9DAKb5T6TzYRFBB4/05_ficha_enlaces_quimicos.webp',
  caption: 'Ficha visual: enlaces químicos'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/oVi6a7zjfiG626jT4dxRqt/07_guia_visual_oxidos.webp',
  caption: 'Guía visual de óxidos'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/dXvyyxHpqRi75sa9WcqKyy/08_evaluacion_oxidos_a_b.webp',
  caption: 'Evaluación de óxidos: versiones A y B'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/nErJPi2pwNRrU8XWGJA5TS/09_guia_estructura_atomica.webp',
  caption: 'Guía visual de estructura atómica'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/szetLvTtB9aJjPh2TAcYXx/10_estados_materia.webp',
  caption: 'Actividades sobre los estados de la materia'
}];
const AUTOPLAY_MS = 4000;
export function MaterialPreview() {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [paused, setPaused] = useState(false);
  const interactionTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const goTo = useCallback((next: number, dir: number) => {
    setDirection(dir);
    setIndex((next + samples.length) % samples.length);
  }, []);

  // Any manual interaction pauses autoplay briefly so the reader stays in control.
  const registerInteraction = useCallback(() => {
    setPaused(true);
    if (interactionTimeout.current) clearTimeout(interactionTimeout.current);
    interactionTimeout.current = setTimeout(() => setPaused(false), 8000);
  }, []);
  const next = useCallback(() => goTo(index + 1, 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1, -1), [goTo, index]);
  useEffect(() => {
    if (paused) return;
    const timer = setTimeout(() => goTo(index + 1, 1), AUTOPLAY_MS);
    return () => clearTimeout(timer);
  }, [index, paused, goTo]);
  useEffect(() => () => {
    if (interactionTimeout.current) clearTimeout(interactionTimeout.current);
  }, []);
  const current = samples[index];
  return <section className="relative py-8 md:py-16 px-4 bg-chem-ice overflow-hidden" data-id="element-107" data-mp="34y2b5vl9t1yb">
      <div aria-hidden="true" className="chem-nodes pointer-events-none absolute inset-0" data-id="element-108" data-mp="g68a34wg7lt0" />

      <div className="relative max-w-5xl mx-auto" data-id="element-109" data-mp="k5qqpoh8laq1">
        <div className="max-w-3xl mx-auto text-center mb-6 md:mb-10" data-id="element-110" data-mp="24p2vgfhdj5np">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-2xl md:text-4xl font-black text-chem-navy leading-tight mb-3" data-id="element-111" data-mp="24r704zn3k8p1">
            Mira el material por dentro
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-chem-navy/70 font-bold text-sm md:text-lg leading-relaxed" data-id="element-112" data-mp="1qvv4mq14ae72">
            Estas son algunas de las fichas, actividades y evaluaciones que
            recibirás listas para imprimir y usar en tus clases de Química.
          </motion.p>
        </div>

        <div className="relative" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocusCapture={() => setPaused(true)} onBlurCapture={() => setPaused(false)} role="group" aria-roledescription="carrusel" aria-label="Muestras del material de Química Visual" data-id="element-113" data-mp="37b67jyll37kd">
          <div className="bg-white rounded-3xl border border-chem-petrol/12 shadow-2xl shadow-chem-navy/10 p-4 md:p-8" data-id="element-114" data-mp="1q07kvq1vqfuw">
            <div className="relative h-[320px] sm:h-[420px] md:h-[560px] overflow-hidden rounded-xl" data-id="element-115" data-mp="1uyirlrwvoa8w">
              <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-116" data-mp="1ga4t1t6hv7y5">
                <motion.img key={current.src} src={current.src} alt={current.caption} custom={direction} initial={{
                opacity: 0,
                x: direction * 28
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: direction * -28
              }} transition={{
                duration: 0.28,
                ease: [0.23, 1, 0.32, 1]
              }} loading={index === 0 ? 'eager' : 'lazy'} decoding="async" drag="x" dragConstraints={{
                left: 0,
                right: 0
              }} dragElastic={0.12} onDragStart={registerInteraction} onDragEnd={(_, info) => {
                if (info.offset.x < -60) next();else if (info.offset.x > 60) prev();
              }} className="absolute inset-0 w-full h-full object-contain select-none cursor-grab active:cursor-grabbing" data-id="element-117" data-mp="1l2xv29akw26j" />
              </AnimatePresence>
            </div>

            <p className="text-center text-chem-navy font-black text-sm md:text-lg mt-4 md:mt-6" data-id="element-118" data-mp="1beahd05502fb">
              {current.caption}
            </p>
            <p className="text-center text-chem-navy/45 font-bold text-[10px] md:text-xs mt-1 uppercase tracking-widest" aria-live="polite" data-id="element-119" data-mp="16gs8f9r4dn7b">
              Muestra {index + 1} de {samples.length}
            </p>
          </div>

          <button type="button" onClick={() => {
          registerInteraction();
          prev();
        }} aria-label="Muestra anterior" className="absolute left-1 md:-left-5 top-1/2 -translate-y-1/2 w-9 h-9 md:w-12 md:h-12 rounded-full bg-white/90 backdrop-blur border border-chem-petrol/15 text-chem-petrol shadow-lg flex items-center justify-center hover:bg-white hover:text-chem-navy transition-colors" data-id="element-120" data-mp="1nhd2lkt9c41n">
            <ChevronLeftIcon className="w-5 h-5 md:w-6 md:h-6" data-id="element-121" data-mp="2b65rwyfdjdna" />
          </button>
          <button type="button" onClick={() => {
          registerInteraction();
          next();
        }} aria-label="Muestra siguiente" className="absolute right-1 md:-right-5 top-1/2 -translate-y-1/2 w-9 h-9 md:w-12 md:h-12 rounded-full bg-white/90 backdrop-blur border border-chem-petrol/15 text-chem-petrol shadow-lg flex items-center justify-center hover:bg-white hover:text-chem-navy transition-colors" data-id="element-122" data-mp="2b88tmfpw2v7u">
            <ChevronRightIcon className="w-5 h-5 md:w-6 md:h-6" data-id="element-123" data-mp="342yj341hj6dd" />
          </button>
        </div>

        <div className="flex justify-center items-center gap-2 mt-5 md:mt-7" data-id="element-124" data-mp="2z8u7g7h9ya5y">
          {samples.map((sample, i) => <button key={sample.src} type="button" onClick={() => {
          registerInteraction();
          goTo(i, i > index ? 1 : -1);
        }} aria-label={`Ver muestra ${i + 1}: ${sample.caption}`} aria-current={i === index} className={`rounded-full transition-colors ${i === index ? 'w-6 h-2 bg-chem-petrol' : 'w-2 h-2 bg-chem-navy/20 hover:bg-chem-turquoise'}`} data-id="element-125" data-mp="2q9fm1mfyspsb" data-mpi={i} />)}
        </div>

        <p className="text-center text-chem-petrol font-bold text-[11px] md:text-base mt-6 md:mt-8 tracking-wide" data-id="element-126" data-mp="22hs0lk1h26xf">
          Fichas visuales · Actividades A/B · Evaluaciones · Recursos listos
          para imprimir
        </p>
      </div>
    </section>;
}